import { Layout, Menu } from "antd";
import { TFunction } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Location } from "react-router-dom";

import colors from "../../../themes/colors";

import { PermissionScope, useAuth } from "@/modules/auth";
import { HeadSider } from "./HeadSider";
import LeftNavLink from "./LeftNavItem";
import {
  NavigationItem,
  navigationItems,
  NavigationSection,
} from "./navigationItems";

const { Sider } = Layout;
const { SubMenu, Item } = Menu;

const styleMenuItem: React.CSSProperties = {
  paddingLeft: "40px",
  margin: "0px",
};

const styleSider = (collapsed: boolean): React.CSSProperties => {
  return {
    overflowX: "hidden",
    overflowY: collapsed ? "hidden" : "scroll",
    height: "100%",
    borderRight: `1px solid ${colors.lightestGray}`,
    backgroundColor: colors.lightestEverGray,
  };
};

const hasAccessToNavigationItem =
  (permissionScope: PermissionScope) => (child: NavigationItem) => {
    const hasRequiredScope =
      !child.requiredScope || child.requiredScope.includes(permissionScope);

    const hasFeatureFlag =
      !child.featureFlag || localStorage.getItem(child.featureFlag);

    return hasRequiredScope && hasFeatureFlag;
  };

const renderNavigationSection =
  (
    permissionsScope: PermissionScope,
    texts: { label: (name: string) => string }
  ) =>
  (navigationSection: NavigationSection) =>
    (
      <SubMenu
        key={navigationSection.label}
        title={texts.label(navigationSection.label) || navigationSection.label}
        icon={navigationSection.icon && <navigationSection.icon />}
      >
        {navigationSection.children
          .filter(hasAccessToNavigationItem(permissionsScope))
          .map((child: NavigationItem, index: React.Key) => {
            return (
              <Item
                data-test={`${child.label.replace(/\s/g, "")}-link`}
                key={`${navigationSection.label}-${index}`}
                style={styleMenuItem}
              >
                {"component" in child ? (
                  <child.component />
                ) : (
                  <LeftNavLink item={child} />
                )}
              </Item>
            );
          })}
      </SubMenu>
    );

/**
 * Based on the current location it checks if the current path corresponds
 * to a child of a section, if found it returns its key and key of the section.
 */
const findActiveKeys = (location: Location) => {
  let activeKey = "";
  let openKey = navigationItems[1].label;
  navigationItems.forEach((section) => {
    if ("children" in section) {
      section.children.forEach((child: NavigationItem, index: number) => {
        if (
          "path" in child &&
          location.pathname.startsWith(child.path as string)
        ) {
          activeKey = `${section.label}-${index}`;
          openKey = section.label;
        }
      });
    }
  });

  return { activeKey, openKey };
};

const getTexts = (t: TFunction<"Navigation", undefined>) => ({
  label: (labelName: string) => t(`navigation.${labelName}`, ""),
});

const LeftNavigation = () => {
  const { getOperatorPermissionScope } = useAuth();
  const location = useLocation();
  const keys = findActiveKeys(location);

  const { t } = useTranslation("Navigation");
  const texts = getTexts(t);

  const [collapsed, setCollapsed] = useState(false);

  const onCollapse = () => {
    setCollapsed(!collapsed);
  };

  const permissionsScope = getOperatorPermissionScope();

  // safeguard in case the user is not logged in
  if (!permissionsScope) {
    return null;
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={56}
      style={styleSider(collapsed)}
    >
      <HeadSider collapsed={collapsed} onCollapse={onCollapse} />
      <Menu
        mode="inline"
        selectedKeys={[keys.activeKey]}
        defaultOpenKeys={navigationItems.map(({ label }) => label)}
        inlineIndent={16}
      >
        {navigationItems
          .filter((navigationSection) =>
            navigationSection.children.some(
              hasAccessToNavigationItem(permissionsScope)
            )
          )
          .map(renderNavigationSection(permissionsScope, texts))}
      </Menu>
    </Sider>
  );
};

export default LeftNavigation;
